import React, { useState } from 'react';
import { confirmResetPassword } from '@aws-amplify/auth';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';

const ConfirmResetPassword = () => {
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  function getCognitoErrorCode(error) {
    if (error?.code) return error.code;
    if (error?.name) return error.name;
    if (error?.['x-amzn-errortype']) return error['x-amzn-errortype'];
    return '';
  }

  function getCognitoErrorMessage(error) {
    if (error?.message) return error.message;
    if (error?.['x-amzn-errormessage']) return error['x-amzn-errormessage'];
    return '';
  }

  function isInvalidCodeError(error) {
    const code = getCognitoErrorCode(error).toLowerCase();
    const msg = getCognitoErrorMessage(error).toLowerCase();
    if (code === 'codeexpiredexception') return true;
    if (code === 'invalidparameterexception') return true;
    if (msg.includes('invalid code') || msg.includes('expired code')) return true;
    return false;
  }

  function isPasswordValidationError(error) {
    const msg = getCognitoErrorMessage(error).toLowerCase();
    return msg.includes('password') || msg.includes('requirements');
  }

  function mapConfirmResetErrorMessage(error) {
    if (isInvalidCodeError(error)) {
      return 'Invalid or expired verification code. Please request a new code.';
    }
    if (isPasswordValidationError(error)) {
      return 'Password does not meet requirements. Please ensure it has at least 8 characters with uppercase, lowercase, and numbers.';
    }
    return 'Failed to reset password. Please try again or contact support.';
  }

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });
      setSuccess(true);
      // Wait a moment to show success message before redirecting
      setTimeout(() => {
        navigate('/signin');
      }, 2000);
    } catch (error) {
      console.error('Error confirming password reset:', error.message);
      setError(mapConfirmResetErrorMessage(error));
    }
  };

  if (!email) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: 'var(--surface)' 
      }}>
        <div className="card" style={{ textAlign: 'center', padding: 'var(--spacing-xl)' }}>
          <h2 style={{ marginBottom: 'var(--spacing-md)' }}>Invalid Access</h2>
          <p style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--text-secondary)' }}>
            Please initiate the password reset process from the sign-in page.
          </p>
          <Link to="/signin" className="button">
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--surface)' }}>
      <div style={{ 
        background: 'linear-gradient(135deg, var(--primary-color), var(--primary-dark))',
        padding: 'var(--spacing-lg) 0',
        marginBottom: 'var(--spacing-2xl)'
      }}>
        <div className="container" style={{ 
          paddingLeft: 'var(--spacing-md)',
          paddingRight: 'var(--spacing-md)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '80vh'
          }}>
            {/* Confirm Reset Password Card */}
            <div style={{ 
              width: '400px',
              maxWidth: '100%'
            }}>
              <div style={{ marginBottom: 'var(--spacing-xl)', textAlign: 'center' }}>
                <Logo size={100} />
              </div>
              <div className="card" style={{ 
                padding: 'var(--spacing-xl)',
                backgroundColor: 'white',
                border: 'none'
              }}>
                <h2 style={{ 
                  marginBottom: 'var(--spacing-md)', 
                  textAlign: 'center',
                  fontSize: 'var(--font-size-xl)'
                }}>Reset Your Password</h2>
                <p style={{ 
                  color: 'var(--text-secondary)', 
                  textAlign: 'center',
                  marginBottom: 'var(--spacing-lg)',
                  fontSize: 'var(--font-size-md)'
                }}>
                  Enter the verification code sent to {email} and your new password.
                </p>

                <form onSubmit={handleConfirmReset} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                  {error && (
                    <div style={{ 
                      padding: 'var(--spacing-sm)',
                      backgroundColor: 'var(--error)',
                      color: 'white',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)'
                    }}>
                      {error}
                    </div>
                  )}

                  {success && (
                    <div style={{ 
                      padding: 'var(--spacing-sm)',
                      backgroundColor: 'var(--success)',
                      color: 'white',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)'
                    }}>
                      Password reset successful! Redirecting to sign in...
                    </div>
                  )}

                  <input
                    type="text"
                    placeholder="Verification Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    className="input"
                  />

                  <input
                    type="password"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="input"
                  />

                  <button type="submit" className="button" style={{ width: '100%' }}>
                    Reset Password
                  </button>

                  <div style={{ textAlign: 'center', marginTop: 'var(--spacing-sm)' }}>
                    <Link 
                      to="/reset-password" 
                      style={{ 
                        color: 'var(--text-secondary)',
                        textDecoration: 'none',
                        fontSize: 'var(--font-size-sm)'
                      }}
                    >
                      ← Back to Reset Password
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmResetPassword; 